import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { Link, withRouter } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import PropTypes from 'prop-types';
import Select from 'react-select';

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Import Datatable
import "../../assets/scss/datatables.scss";

// Import API helper
import httprequester from '../../lib/httprequester';

// Status badge component for registration status
const RegistrationStatusBadge = ({ isApprove }) => {
  const getBadgeClass = (value) => {
    if (value === null) return 'bg-warning badge-warning text-dark';
    if (value === 1) return 'bg-success badge-success';
    if (value === 0) return 'bg-danger badge-danger';
    return 'bg-secondary badge-secondary';
  };

  const getStatusText = (value) => {
    if (value === null) return 'PENDING';
    if (value === 1) return 'APPROVED';
    if (value === 0) return 'REJECTED';
    return 'UNKNOWN';
  };

  return (
    <span className={`badge ${getBadgeClass(isApprove)}`}>
      {getStatusText(isApprove)}
    </span>
  );
};

RegistrationStatusBadge.propTypes = {
  isApprove: PropTypes.number
};

// Action buttons component
const ActionButtons = ({ registration, onApprove, onReject, onDelete, onRestore }) => {
  if (registration.is_deleted) {
    return (
      <Button
        color="outline-success"
        size="sm"
        onClick={() => onRestore(registration)}
        title="Restore Registration"
      >
        <i className="bx bx-undo"></i> Restore
      </Button>
    );
  }

  return (
    <div className="d-flex gap-1">
      {registration.is_approve === null && (
        <>
          <Button
            color="success"
            size="sm"
            onClick={() => onApprove(registration)}
            title="Approve Registration"
          >
            <i className="bx bx-check"></i>
          </Button>

          <Button
            color="danger"
            size="sm"
            onClick={() => onReject(registration)}
            title="Reject Registration"
          >
            <i className="bx bx-x"></i>
          </Button>
        </>
      )}

      <Button
        color="outline-danger"
        size="sm"
        onClick={() => onDelete(registration)}
        title="Delete Registration"
      >
        <i className="bx bx-trash"></i>
      </Button>
    </div>
  );
};

ActionButtons.propTypes = {
  registration: PropTypes.shape({
    webinar_register_id: PropTypes.number.isRequired,
    user_name: PropTypes.string.isRequired,
    is_approve: PropTypes.number,
    is_deleted: PropTypes.bool
  }).isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired
};

// Header checkbox that also reflects a "some but not all selected" state,
// which plain HTML checkboxes can only express via the imperative `indeterminate` DOM property.
const SelectAllCheckbox = ({ checked, indeterminate, disabled, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label="Select all pending registrations on this page"
    />
  );
};

SelectAllCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
};

const ListCMERegistration = ({ history }) => {
  // Utility function to generate unique keys
  const generateUniqueKey = (registration, suffix = '') => {
    const safeId = registration.webinar_register_id || registration.id || 'temp';
    const safeType = registration.type || 'REGISTER';
    const timestamp = Date.now();
    return `${safeId}-${safeType}-${timestamp}${suffix}`;
  };

  // Utility function to ensure unique keys in the list
  const ensureUniqueKeys = (list) => {
    const seenKeys = new Set();
    return list.map((item, index) => {
      let uniqueKey = item.uniqueKey;

      // If no uniqueKey or if it's a duplicate, generate a new one
      if (!uniqueKey || seenKeys.has(uniqueKey)) {
        uniqueKey = generateUniqueKey(item, `-${index}-${Date.now()}`);
      }

      seenKeys.add(uniqueKey);
      return { ...item, uniqueKey };
    });
  };

  // State management
  const [registrationList, setRegistrationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    webinar_id: '',
    type: '',
    is_approve: '',
    search: '',
    deleted: false,
    page: 1,
    limit: 50,
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 50
  });
  const [pageInput, setPageInput] = useState('1');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproveModal, setBulkApproveModal] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [summaryCounts, setSummaryCounts] = useState({
    registerPending: 0,
    waitingPending: 0,
    approved: 0,
    rejected: 0,
    total: 0
  });
  const [webinarOptions, setWebinarOptions] = useState([]);
  const [webinarLoading, setWebinarLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    registration: null
  });
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [restoreModal, setRestoreModal] = useState({
    show: false,
    registration: null
  });
  const [restoring, setRestoring] = useState(false);
  const [actionModal, setActionModal] = useState({
    show: false,
    type: '',
    registration: null,
    message: ''
  });
  const [selectedRejectReason, setSelectedRejectReason] = useState('');

  // Fetch webinar options from backend
  const fetchWebinarOptions = useCallback(async () => {
    try {
      setWebinarLoading(true);
      setError(''); // Clear any previous errors

      const response = await httprequester.getAsync('webinar/cms/webinars-dropdown');

      if (response.data.data && Array.isArray(response.data.data)) {
        const formattedWebinars = response.data.data.map(webinar => ({
          webinar_id: webinar.webinar_id,
          title: webinar.title,
          date: webinar.date,
          status: webinar.status
        }));
        setWebinarOptions(formattedWebinars);
      } else {
        console.error('Invalid webinar data format:', response.data);
        setWebinarOptions([]);
      }
    } catch (error) {
      console.error('Error fetching webinar options:', error);
      setWebinarOptions([]);
      setError('Failed to load webinar options');
    } finally {
      setWebinarLoading(false);
    }
  }, []);

  // Fetch participants from backend
  const fetchParticipantsFromAPI = useCallback(async (overrideFilters) => {
    setLoading(true);
    setError('');

    try {
      const f = overrideFilters || filters;
      const queryParams = new URLSearchParams({
        page: String(f.page || 1),
        limit: String(f.limit || 50)
      });

      if (f.search) queryParams.append('search', f.search.trim());
      if (f.webinar_id) queryParams.append('webinar_id', String(f.webinar_id));
      if (f.type) queryParams.append('type', f.type);
      if (f.is_approve !== '') {
        const statusParam = f.is_approve === 'null' ? 'null' : String(f.is_approve);
        queryParams.append('status', statusParam);
      }
      // Deleted participants are excluded by default; this opts into viewing the trash instead.
      if (f.deleted) queryParams.append('deleted', '1');

      const response = await httprequester.getAsync(`webinar/cms/webinars/participants?${queryParams.toString()}`);
      const payload = response?.data?.data || {};
      const list = Array.isArray(payload.data) ? payload.data : [];

      // Map API id to existing key usage to avoid broader refactors
      // Ensure unique IDs by combining id with type if needed
      const normalizedList = list.map((item, index) => {
        // Ensure we have a valid ID, fallback to index if needed
        const safeId = item.id || `temp-${index}`;
        const safeType = item.type || 'REGISTER';

        const normalizedItem = {
          ...item,
          webinar_register_id: safeId,
          is_deleted: item.is_deleted === 1 || item.is_deleted === true || Boolean(item.deleted_at)
        };

        // Generate unique key using the utility function
        normalizedItem.uniqueKey = generateUniqueKey(normalizedItem, `-${index}`);

        return normalizedItem;
      });

      setRegistrationList(ensureUniqueKeys(normalizedList));
      setSelectedIds(new Set());
      setPagination({
        current_page: parseInt(payload.page) || 1,
        total_pages: parseInt(payload.lastPage) || 1,
        total_items: parseInt(payload.total) || 0,
        items_per_page: parseInt(payload.perPage) || (f.limit || 50)
      });

      const countsFromPayload = payload.counts || {};
      const normalizedCounts = {
        registerPending: Number(countsFromPayload.registerPending) || 0,
        waitingPending: Number(countsFromPayload.waitingPending) || 0,
        approved: Number(countsFromPayload.approved) || 0,
        rejected: Number(countsFromPayload.rejected) || 0,
        total: Number(countsFromPayload.total) || 0
      };

      // Fallback to page data if API counts are unavailable
      const fallbackCounts = {
        registerPending: normalizedList.filter(r => ((r.type || 'REGISTER') === 'REGISTER') && r.is_approve === null).length,
        waitingPending: normalizedList.filter(r => r.type === 'WAITING_LIST' && r.is_approve === null).length,
        approved: normalizedList.filter(r => r.is_approve === 1).length,
        rejected: normalizedList.filter(r => r.is_approve === 0).length,
        total: parseInt(payload.total, 10) || normalizedList.length
      };

      setSummaryCounts(payload.counts ? normalizedCounts : fallbackCounts);
    } catch (err) {
      console.error('Error fetching participants:', err);
      setError('Error fetching participants');
      setRegistrationList([]);
      setPagination({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        items_per_page: (overrideFilters?.limit ?? filters.limit) || 50
      });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Webinar options for react-select
  const webinarSelectOptions = [
    { value: '', label: 'All Webinars' },
    ...webinarOptions.map(webinar => {
      const safeDate = webinar.date ? new Date(webinar.date) : null;
      const formattedDate = safeDate && !isNaN(safeDate)
        ? safeDate.toLocaleDateString('id-ID')
        : 'Date not available';

      return {
        value: webinar.webinar_id,
        label: webinar.title
      };
    })
  ];

  // Type options for filtering
  const typeOptions = [
    { value: 'REGISTER', label: 'Register' },
    { value: 'WAITING_LIST', label: 'Waiting List' }
  ];

  // Reject reason options
  const rejectReasonOptions = [
    { value: 'Dokumentasi Tidak Lengkap', label: 'Dokumentasi Tidak Lengkap' },
    { value: 'Kredensial Tidak Valid', label: 'Kredensial Tidak Valid' },
    { value: 'Registrasi Ganda', label: 'Registrasi Ganda' },
    { value: 'Tidak Memenuhi Syarat', label: 'Tidak Memenuhi Syarat' },
    { value: 'Pendaftaran Ditutup', label: 'Pendaftaran Ditutup' },
    { value: 'Masalah Teknis', label: 'Masalah Teknis' },
    { value: 'Lainnya', label: 'Lainnya' }
  ];

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': {
        border: '1px solid #80bdff'
      },
      borderRadius: '0.375rem',
      fontSize: '0.875rem'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
      },
      fontSize: '0.875rem',
      padding: '8px 12px'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      border: '1px solid #ced4da',
      borderRadius: '0.375rem',
      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
      fontSize: '0.875rem'
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#495057'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '0.875rem'
    })
  };

  // Initial load only
  useEffect(() => {
    fetchWebinarOptions();
    fetchParticipantsFromAPI();
  }, []);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      if (key !== 'page') next.page = 1;
      if (key === 'page' || key === 'limit') {
        fetchParticipantsFromAPI(next);
      }
      return next;
    });
  };

  // Keep the "go to page" input in sync with the actual current page
  useEffect(() => {
    setPageInput(String(pagination.current_page));
  }, [pagination.current_page]);

  const handleGoToPage = () => {
    const maxPage = pagination.total_pages || 1;
    let target = parseInt(pageInput, 10);
    if (Number.isNaN(target)) target = pagination.current_page;
    target = Math.min(Math.max(target, 1), maxPage);
    setPageInput(String(target));
    if (target !== pagination.current_page) {
      handleFilterChange('page', target);
    }
  };

  // Handle webinar selection change
  const handleWebinarChange = (selectedOption) => {
    handleFilterChange('webinar_id', selectedOption ? selectedOption.value : '');
  };

  // Handle type selection change
  const handleTypeChange = (e) => {
    handleFilterChange('type', e.target.value);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchParticipantsFromAPI();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      webinar_id: '',
      type: '',
      is_approve: '',
      search: '',
      deleted: false,
      page: 1,
      limit: 50,
      sort_by: 'created_at',
      sort_order: 'desc'
    });
  };

  // Toggle between the active participant list and the soft-deleted trash view
  const handleToggleDeletedView = () => {
    const next = { ...filters, deleted: !filters.deleted, page: 1 };
    setFilters(next);
    setSelectedIds(new Set());
    fetchParticipantsFromAPI(next);
  };

  const handleApprove = (registration) => {
    setActionModal({
      show: true,
      type: 'approve',
      registration,
      message: `Approve registration for ${registration.user_name}?`
    });
  };

  const handleReject = (registration) => {
    setSelectedRejectReason(''); // Reset reject reason selection
    setActionModal({
      show: true,
      type: 'reject',
      registration,
      message: `Reject registration for ${registration.user_name}?`
    });
  };

  const handleDelete = (registration) => {
    setDeleteReason('');
    setDeleteModal({
      show: true,
      registration
    });
  };

  const handleRestore = (registration) => {
    setRestoreModal({
      show: true,
      registration
    });
  };

  // Stable per-row key for selection tracking (webinar_register_id alone can
  // collide between REGISTER and WAITING_LIST rows, so type is included).
  const getRowKey = (registration) => `${registration.webinar_register_id}-${registration.type || 'REGISTER'}`;

  const pendingOnPage = registrationList.filter(r => !r.is_deleted && r.is_approve === null);
  const selectedPendingCount = pendingOnPage.filter(r => selectedIds.has(getRowKey(r))).length;
  const allPendingSelected = pendingOnPage.length > 0 && selectedPendingCount === pendingOnPage.length;
  const somePendingSelected = selectedPendingCount > 0 && !allPendingSelected;
  const selectedRegistrations = registrationList.filter(r => selectedIds.has(getRowKey(r)));

  const toggleSelectRow = (registration) => {
    const key = getRowKey(registration);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPendingSelected) {
        pendingOnPage.forEach(r => next.delete(getRowKey(r)));
      } else {
        pendingOnPage.forEach(r => next.add(getRowKey(r)));
      }
      return next;
    });
  };

  const handleBulkApproveClick = () => {
    if (selectedRegistrations.length === 0) return;
    setBulkApproveModal(true);
  };

  // Approves each selected registration sequentially (not Promise.all) so a
  // failure is attributable to one participant and the backend isn't hit
  // with a burst of concurrent writes when many rows are selected.
  const handleBulkApproveConfirm = async () => {
    setBulkApproveModal(false);
    setBulkApproving(true);

    const targets = selectedRegistrations;
    let successCount = 0;
    const failedNames = [];

    for (const registration of targets) {
      try {
        const participantType = registration.type === 'WAITING_LIST' ? 'WAITING_LIST' : 'REGISTER';
        // eslint-disable-next-line no-await-in-loop
        await httprequester.postAsync('webinar/cms/participants/approve', {
          participant_id: registration.webinar_register_id,
          participant_type: participantType,
          is_approve: 1
        });
        successCount += 1;
      } catch (err) {
        console.error('Bulk approve error for registration', registration.webinar_register_id, err);
        failedNames.push(registration.user_name || `#${registration.webinar_register_id}`);
      }
    }

    setBulkApproving(false);
    setSelectedIds(new Set());

    if (failedNames.length === 0) {
      setSuccess(`${successCount} registration(s) approved successfully`);
    } else {
      setError(`${successCount} approved, ${failedNames.length} failed (${failedNames.join(', ')}). Please retry the failed ones.`);
    }

    // Waiting-list approvals can promote participants into REGISTER and shift
    // counts/pagination, so refetch instead of patching local state.
    fetchParticipantsFromAPI();
  };

  // Handle action confirmation
  const handleActionConfirm = async () => {
    const { type, registration } = actionModal;

    // Validate reject reason if rejecting
    if (type === 'reject' && !selectedRejectReason) {
      setError('Silakan pilih alasan penolakan');
      return;
    }

    try {
      const isApprove = type === 'approve' ? 1 : 0;
      const participantType = registration.type === 'WAITING_LIST' ? 'WAITING_LIST' : 'REGISTER';

      const requestBody = {
        participant_id: registration.webinar_register_id,
        participant_type: participantType,
        is_approve: isApprove
      };

      // Add reject_reason to request body if rejecting
      if (type === 'reject') {
        requestBody.reject_reason = selectedRejectReason;
      }

      const response = await httprequester.postAsync('webinar/cms/participants/approve', requestBody);

      // Handle the new structured API response
      if (response.data && response.data.success) {
        const { data: responseData, message } = response.data;
        const { action, participant_type, current_status, waiting_list } = responseData;

        if (action === 'APPROVED_AND_PROMOTED' && participant_type === 'WAITING_LIST') {
          // WAITING_LIST participant was approved and promoted to main registration
          setRegistrationList(prev => {
            const registrationToUpdate = prev.find(reg =>
              reg.webinar_register_id === waiting_list.webinar_waiting_id && reg.type === 'WAITING_LIST'
            );

            if (registrationToUpdate) {
              const updatedRegistrationBase = {
                ...registrationToUpdate,
                id: responseData.new_registration?.webinar_register_id,
                type: 'REGISTER',
                is_approve: current_status,
                webinar_register_id: responseData.new_registration?.webinar_register_id
              };

              const updatedRegistration = {
                ...updatedRegistrationBase,
                // Generate new unique key for the updated registration
                uniqueKey: generateUniqueKey(updatedRegistrationBase, '-promoted')
              };

              // Create new array without the old item, then add the updated one
              const newList = [
                ...prev.filter(reg =>
                  !(reg.webinar_register_id === waiting_list.webinar_waiting_id && reg.type === 'WAITING_LIST')
                ),
                updatedRegistration
              ];

              // Ensure all keys are unique
              return ensureUniqueKeys(newList);
            }

            return prev;
          });
        } else if (action === 'APPROVED' || action === 'REJECTED') {
          // Regular registration approval/rejection
          setRegistrationList(prev => {
            const updatedList = prev.map(reg => {
              if (reg.webinar_register_id === registration.webinar_register_id && reg.type === participant_type) {
                return {
                  ...reg,
                  is_approve: current_status,
                  // Update unique key to ensure React re-renders properly
                  uniqueKey: generateUniqueKey(reg, '-updated')
                };
              }
              return reg;
            });

            // Ensure all keys are unique
            return ensureUniqueKeys(updatedList);
          });
        }
      }

      setSuccess(response.data?.message || `Registration ${type === 'approve' ? 'approved' : 'rejected'} successfully`);
    } catch (error) {
      console.error(`Error ${type}ing registration:`, error);

      // Handle specific error responses
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 400:
            setError(data?.message || 'Invalid request parameters');
            break;
          case 404:
            setError(data?.message || 'Registration not found');
            break;
          case 409:
            setError(data?.message || 'User already registered for this webinar');
            break;
          case 500:
            setError(data?.message || 'Internal server error occurred');
            break;
          default:
            setError(`Failed to ${type} registration. Please try again.`);
        }
      } else {
        setError(`Failed to ${type} registration. Please try again.`);
      }
    }

    setActionModal({ show: false, type: '', registration: null, message: '' });
  };

  // Soft-delete registration: the backend sets deleted_at instead of removing the
  // row, so it stays recoverable via the "Show Deleted" trash view (audit trail).
  const handleDeleteConfirm = async () => {
    const { registration } = deleteModal;
    if (!registration) return;

    const reason = deleteReason.trim();
    if (!reason) {
      setError('A reason is required to delete a participant.');
      return;
    }

    setDeleting(true);

    try {
      const participantType = registration.type === 'WAITING_LIST' ? 'WAITING_LIST' : 'REGISTER';
      await httprequester.deleteAsync(
        `webinar/cms/participants/${participantType}/${registration.webinar_register_id}?reason=${encodeURIComponent(reason)}`
      );

      setSuccess(`Registration for ${registration.user_name} deleted successfully`);
      setDeleteModal({ show: false, registration: null });
      setDeleteReason('');

      // Refetch so pagination/summary counts stay accurate instead of drifting locally
      fetchParticipantsFromAPI();
    } catch (err) {
      console.error('Error deleting registration:', err);
      setError(err?.response?.data?.message || 'Failed to delete registration. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Restore a previously soft-deleted registration back into the active list
  const handleRestoreConfirm = async () => {
    const { registration } = restoreModal;
    if (!registration) return;

    setRestoring(true);

    try {
      const participantType = registration.type === 'WAITING_LIST' ? 'WAITING_LIST' : 'REGISTER';
      await httprequester.postAsync(
        `webinar/cms/participants/${participantType}/${registration.webinar_register_id}/restore`,
        {}
      );

      setSuccess(`Registration for ${registration.user_name} restored successfully`);
      setRestoreModal({ show: false, registration: null });

      fetchParticipantsFromAPI();
    } catch (err) {
      console.error('Error restoring registration:', err);
      setError(err?.response?.data?.message || 'Failed to restore registration. Please try again.');
    } finally {
      setRestoring(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Table columns configuration
  const columns = [
    {
      key: 'select',
      label: (
        <SelectAllCheckbox
          checked={allPendingSelected}
          indeterminate={somePendingSelected}
          disabled={pendingOnPage.length === 0}
          onChange={toggleSelectAllOnPage}
        />
      ),
      render: (registration) => (
        !registration.is_deleted && registration.is_approve === null ? (
          <input
            type="checkbox"
            checked={selectedIds.has(getRowKey(registration))}
            onChange={() => toggleSelectRow(registration)}
            aria-label={`Select ${registration.user_name}`}
          />
        ) : null
      )
    },
    {
      key: 'user_info',
      label: 'User Information',
      render: (registration) => (
        <div>
          <div className="fw-bold">{registration.user_name}</div>
          <small className="text-muted d-block">{registration.email}</small>
          <small className="text-muted d-block">NIK: {registration.NIK}</small>
          <small className="text-muted d-block">{registration.user_membership}</small>
          <small className="text-muted d-block">{registration.user_handphone}</small>
        </div>
      )
    },
    {
      key: 'webinar_title',
      label: 'Webinar',
      render: (registration) => (
        <div>
          <div
            className="fw-bold text-truncate"
            style={{ maxWidth: '300px' }}
            title={registration.webinar_title}
          >
            {registration.webinar_title}
          </div>
        </div>
      )
    },
    {
      key: 'registration_date',
      label: 'Registration Date',
      render: (registration) => formatDate(registration.created_at)
    },
    {
      key: 'type',
      label: 'Type',
      render: (registration) => (
        registration.type === 'WAITING_LIST' ? 'Waiting List' : 'Register'
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (registration) => (
        registration.is_deleted ? (
          <span className="badge bg-secondary badge-secondary">DELETED</span>
        ) : (
          <RegistrationStatusBadge isApprove={registration.is_approve} />
        )
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (registration) => (
        <ActionButtons
          registration={registration}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDelete}
          onRestore={handleRestore}
        />
      )
    }
  ];

  // Clear alerts
  const clearAlert = () => setError('');
  const clearSuccess = () => setSuccess('');

  // Export all filtered rows to Excel (uses backend export_all to bypass pagination)
  const handleExportExcel = async () => {
    try {
      const f = filters;
      const queryParams = new URLSearchParams({
        page: '1',
        limit: String(f.limit || 10),
        export_all: '1'
      });

      if (f.search) queryParams.append('search', f.search.trim());
      if (f.webinar_id) queryParams.append('webinar_id', String(f.webinar_id));
      if (f.type) queryParams.append('type', f.type);
      if (f.is_approve !== '') {
        const statusParam = f.is_approve === 'null' ? 'null' : String(f.is_approve);
        queryParams.append('status', statusParam);
      }

      const response = await httprequester.getAsync(`webinar/cms/webinars/participants?${queryParams.toString()}`);
      const payload = response?.data?.data || {};
      const rows = Array.isArray(payload.data) ? payload.data : [];

      if (rows.length === 0) {
        setError('No data to export');
        return;
      }

      const data = rows.map(r => {
        const createdAt = r.created_at ? new Date(r.created_at) : null;
        const createdAtFormatted = createdAt && !isNaN(createdAt)
          ? createdAt.toLocaleString('id-ID', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
          : '';

        const getStatus = (isApprove) => {
          if (isApprove === 1) return 'APPROVED';
          if (isApprove === 0) return 'REJECTED';
          return 'PENDING';
        };

        return {
          'Full Name': r.user_name ?? '',
          'Email': r.email ?? '',
          'Specialist': r.specialist ?? '',
          'Phone': r.user_handphone ?? '',
          'NIK': r.NIK ?? '',
          'Webinar': r.webinar_title ?? '',
          'Registered At': createdAtFormatted,
          'Type': r.type ?? '',
          'Status': getStatus(r.is_approve)
        };
      });

      const mod = await import('xlsx');
      const XLSX = mod.default || mod;
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Registrations');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `cme-registrations-${ts}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (e) {
      console.error('Export error:', e);
      setError('Failed to export to Excel. Please install dependency: npm i xlsx');
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Webinar Registrations | Ruang Ilmiah CMS</title>
        </MetaTags>

        <div className="container-fluid">
          <Breadcrumbs title="Webinar Registrations" breadcrumbItem="CME Online" />

          {/* Filters Section */}
          <Card className="mb-4">
            <CardBody>
              <form onSubmit={(e) => e.preventDefault()}>
                <Row>
                  <Col md="3">
                    <label className="form-label">Webinar</label>
                    <div className="webinar-select-wrapper" style={{ position: 'relative', marginBottom: '0' }}>
                      <div className="d-flex gap-2">
                        <div style={{ flex: 1 }}>
                          <Select
                            isClearable
                            isSearchable
                            placeholder={webinarLoading ? "Loading webinars..." : "Select webinar..."}
                            options={webinarSelectOptions}
                            value={webinarSelectOptions.find(option => option.value === (filters.webinar_id ? parseInt(filters.webinar_id) : '')) || webinarSelectOptions[0]}
                            onChange={handleWebinarChange}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={selectStyles}
                            isLoading={webinarLoading}
                            isDisabled={webinarLoading}
                            noOptionsMessage={() => webinarOptions.length === 0 ? "No webinars available" : "No webinars found"}
                          />
                        </div>
                      </div>
                      {error && webinarOptions.length === 0 && (
                        <small className="text-danger d-block mt-1">
                          Failed to load webinars. Please refresh the page.
                        </small>
                      )}
                      {!error && !webinarLoading && webinarOptions.length === 0 && (
                        <small className="text-muted d-block mt-1">
                          No webinars available at the moment.
                        </small>
                      )}
                    </div>
                  </Col>
                  <Col md="2">
                    <label className="form-label">Type</label>
                    <Input
                      type="select"
                      value={filters.type}
                      onChange={handleTypeChange}
                      style={{ minHeight: '38px' }}
                    >
                      <option value="">All Types</option>
                      {typeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md="2">
                    <label className="form-label">Registration Status</label>
                    <Input
                      type="select"
                      value={filters.is_approve}
                      onChange={(e) => handleFilterChange('is_approve', e.target.value)}
                      style={{ minHeight: '38px' }}
                    >
                      <option value="">All Status</option>
                      <option value="null">Pending</option>
                      <option value="1">Approved</option>
                      <option value="0">Rejected</option>
                    </Input>
                  </Col>
                  <Col md="2">
                    <label className="form-label">Search</label>
                    <Input
                      type="text"
                      placeholder="Search by name, email, NIK..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      style={{ minHeight: '38px' }}
                    />
                  </Col>
                  <Col md="3" className="d-flex align-items-end">
                    <div className="d-flex gap-2">
                      <Button color="primary" type="button" onClick={handleSearch} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : 'Search'}
                      </Button>
                      <Button color="secondary" type="button" onClick={resetFilters}>
                        Reset
                      </Button>
                      <Button
                        color={filters.deleted ? 'dark' : 'outline-dark'}
                        type="button"
                        onClick={handleToggleDeletedView}
                        disabled={loading}
                        title="Toggle between active participants and soft-deleted participants"
                      >
                        <i className="bx bx-trash me-1"></i>
                        {filters.deleted ? 'Back to Active' : 'Show Deleted'}
                      </Button>
                      <Button color="success" type="button" onClick={handleExportExcel} disabled={loading || registrationList.length === 0}>
                        <i className="bx bx-download me-1"></i>
                        Export
                      </Button>
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>

          {/* Summary Cards */}
          <Row className="flex-nowrap">
            <Col style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <Card className="text-center">
                <CardBody>
                  <div className="text-primary">
                    <i className="bx bx-user-plus bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summaryCounts.registerPending}</h4>
                  <p className="text-muted mb-0">Register</p>
                </CardBody>
              </Card>
            </Col>
            <Col style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <Card className="text-center">
                <CardBody>
                  <div className="text-info">
                    <i className="bx bx-time bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summaryCounts.waitingPending}</h4>
                  <p className="text-muted mb-0">Waiting List</p>
                </CardBody>
              </Card>
            </Col>
            <Col style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <Card className="text-center">
                <CardBody>
                  <div className="text-success">
                    <i className="bx bx-check-circle bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summaryCounts.approved}</h4>
                  <p className="text-muted mb-0">Approved</p>
                </CardBody>
              </Card>
            </Col>
            <Col style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <Card className="text-center">
                <CardBody>
                  <div className="text-danger">
                    <i className="bx bx-x-circle bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summaryCounts.rejected}</h4>
                  <p className="text-muted mb-0">Rejected</p>
                </CardBody>
              </Card>
            </Col>
            <Col style={{ flex: '0 0 20%', maxWidth: '20%' }}>
              <Card className="text-center">
                <CardBody>
                  <div className="text-warning">
                    <i className="bx bx-user bx-lg"></i>
                  </div>
                  <h4 className="mt-2">{summaryCounts.total}</h4>
                  <p className="text-muted mb-0">Total</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Results Section */}
          <Card>
            <CardBody>
              {selectedIds.size > 0 && (
                <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded">
                  <div>{selectedIds.size} registration(s) selected</div>
                  <Button
                    color="success"
                    size="sm"
                    onClick={handleBulkApproveClick}
                    disabled={bulkApproving}
                  >
                    {bulkApproving ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="bx bx-check-double me-1"></i>
                        Approve Selected ({selectedIds.size})
                      </>
                    )}
                  </Button>
                </div>
              )}
              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading registrations...</p>
                </div>
              ) : error ? (
                <Alert color="danger" isOpen={!!error} toggle={clearAlert}>
                  {error}
                </Alert>
              ) : registrationList.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bx bx-user-x bx-lg text-muted"></i>
                  <p className="mt-2">No registrations found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        {columns.map((column) => (
                          <th key={column.key}>
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {registrationList.map((registration) => (
                        <tr style={{ cursor: 'auto' }} key={registration.uniqueKey || `${registration.webinar_register_id}-${registration.type || 'REGISTER'}`}>
                          {columns.map((column) => (
                            <td key={`${registration.uniqueKey || registration.webinar_register_id}-${column.key}`}>
                              {column.render ? column.render(registration) : registration[column.key]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Pagination */}
          {pagination.total_items > 0 && (
            <Row className="mt-3 mb-4">
              <Col className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span>
                    Showing {((pagination.current_page - 1) * pagination.items_per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} of{' '}
                    {pagination.total_items} results
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0 text-nowrap">Rows per page</label>
                    <Input
                      type="select"
                      style={{ width: '90px', minHeight: '38px' }}
                      value={filters.limit}
                      onChange={(e) => handleFilterChange('limit', Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Input>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    color="outline-primary"
                    disabled={pagination.current_page === 1}
                    onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                  >
                    Previous
                  </Button>
                  <div className="d-flex align-items-center gap-1">
                    <span>Page</span>
                    <Input
                      type="number"
                      min={1}
                      max={pagination.total_pages}
                      style={{ width: '70px', minHeight: '38px' }}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleGoToPage(); }}
                    />
                    <span>of {pagination.total_pages}</span>
                    <Button color="outline-secondary" onClick={handleGoToPage}>
                      Go
                    </Button>
                  </div>
                  <Button
                    color="outline-primary"
                    disabled={pagination.current_page >= pagination.total_pages}
                    onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.show} toggle={() => setDeleteModal({ show: false, registration: null })}>
        <ModalHeader toggle={() => setDeleteModal({ show: false, registration: null })}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete the registration for &quot;{deleteModal.registration?.user_name}&quot;?
            This participant will be moved to the deleted list and can be restored later.
          </p>
          <label className="form-label">Reason <span className="text-danger">*</span></label>
          <Input
            type="textarea"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Enter the reason for deleting this registration..."
            maxLength={500}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal({ show: false, registration: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteConfirm} disabled={deleting || !deleteReason.trim()}>
            {deleting ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal isOpen={restoreModal.show} toggle={() => setRestoreModal({ show: false, registration: null })}>
        <ModalHeader toggle={() => setRestoreModal({ show: false, registration: null })}>
          Confirm Restore
        </ModalHeader>
        <ModalBody>
          Restore the registration for &quot;{restoreModal.registration?.user_name}&quot; back to the active list?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRestoreModal({ show: false, registration: null })} disabled={restoring}>
            Cancel
          </Button>
          <Button color="success" onClick={handleRestoreConfirm} disabled={restoring}>
            {restoring ? <Spinner size="sm" /> : 'Restore'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={actionModal.show} toggle={() => {
        setSelectedRejectReason('');
        setActionModal({ show: false, type: '', registration: null, message: '' });
      }}>
        <ModalHeader toggle={() => {
          setSelectedRejectReason('');
          setActionModal({ show: false, type: '', registration: null, message: '' });
        }}>
          Confirm Action
        </ModalHeader>
        <ModalBody>
          {actionModal.message}
          {actionModal.type === 'reject' && (
            <div className="mt-3">
              <label className="form-label">Alasan Penolakan <span className="text-danger">*</span></label>
              <Select
                isClearable
                placeholder="Pilih alasan penolakan..."
                options={rejectReasonOptions}
                value={rejectReasonOptions.find(option => option.value === selectedRejectReason) || null}
                onChange={(selectedOption) => setSelectedRejectReason(selectedOption ? selectedOption.value : '')}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={selectStyles}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setSelectedRejectReason('');
            setActionModal({ show: false, type: '', registration: null, message: '' });
          }}>
            Cancel
          </Button>
          <Button
            color={actionModal.type === 'approve' ? 'success' : 'danger'}
            onClick={handleActionConfirm}
          >
            {actionModal.type === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Approve Confirmation Modal */}
      <Modal isOpen={bulkApproveModal} toggle={() => setBulkApproveModal(false)}>
        <ModalHeader toggle={() => setBulkApproveModal(false)}>
          Confirm Bulk Approve
        </ModalHeader>
        <ModalBody>
          Approve {selectedRegistrations.length} selected registration(s)? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBulkApproveModal(false)}>
            Cancel
          </Button>
          <Button color="success" onClick={handleBulkApproveConfirm}>
            Approve Selected
          </Button>
        </ModalFooter>
      </Modal>

      {/* Success Alert */}
      {success && (
        <SweetAlert
          success
          title="Success!"
          onConfirm={clearSuccess}
        >
          {success}
        </SweetAlert>
      )}
    </React.Fragment>
  );
};

ListCMERegistration.propTypes = {
  history: PropTypes.object.isRequired
};

export default withRouter(ListCMERegistration);